// Project monthly progress reports (backend services/projectReport.service.ts, utils/projectReport.util.ts;
// docs/PROJECT_MONTHLY_REPORT.md). The constants mirror the backend's.

export type ReportTemplate = 'KSA' | 'KWT';
export type ReportStatus = 'Draft' | 'Submitted';
export type IssueSection = 'SAFETY' | 'INTERNAL' | 'EXTERNAL';
export type IssueRank = 1 | 2 | 3 | 4;
export type IssueStatus = 'Open' | 'Resolved';
export type KsaCategory = 'main' | 'specialProjects' | 'chargeBack' | 'profitCenter';

export const TEMPLATE_LABELS: Record<ReportTemplate, string> = { KSA: 'Engie KSA', KWT: 'Dhow Kuwait' };

export const RANKS: ReadonlyArray<{ rank: IssueRank; label: string; meaning: string; color: string; text: string }> = [
  { rank: 1, label: 'Critical', meaning: 'Top management action now', color: '#b71c1c', text: '#ffffff' },
  { rank: 2, label: 'High', meaning: 'Management attention this month', color: '#e65100', text: '#ffffff' },
  { rank: 3, label: 'Medium', meaning: 'Project manager handling; watch', color: '#f9a825', text: '#000000' },
  { rank: 4, label: 'Low', meaning: 'For information', color: '#607d8b', text: '#ffffff' },
];
export const rankInfo = (rank: number) => RANKS.find((r) => r.rank === rank);

export const SECTION_LABELS: Record<IssueSection, string> = {
  SAFETY: 'Safety',
  INTERNAL: 'Internal concern',
  EXTERNAL: 'External concern',
};

export const KSA_CATEGORIES: KsaCategory[] = ['main', 'specialProjects', 'chargeBack', 'profitCenter'];
export const KSA_CATEGORY_LABELS: Record<KsaCategory, string> = {
  main: 'Main Contract',
  specialProjects: 'Special Projects',
  chargeBack: 'Charge Back',
  profitCenter: 'Profit Center',
};

export interface ReportIssue {
  issueId?: number;
  /** Screen-only key for new rows. */
  key?: string;
  section: IssueSection;
  /** 0 = not chosen yet (the screen asks for it before saving). */
  rank: IssueRank | 0;
  description: string;
  action: string;
  status: IssueStatus;
  firstPeriodMonth: string;
}

export interface SubcontractorRow {
  name: string;
  scope: string;
  reference: string;
  value: number | null;
  paidPrevious: number | null;
  paidThisPeriod: number | null;
  paidTotal: number | null;
  workDonePeriod: string;
  source: 'SAP' | 'PM';
  poDocEntry: number | null;
}

export interface ReportData {
  cover: { sector: string; client: string; mainContractor: string; consultant: string };
  contract: { reference: string; title: string; scope: string };
  dates: {
    startDate: string | null;
    actualStartDate: string | null;
    originalCompletion: string | null;
    forecastCompletion: string | null;
    latestRenewal: string | null;
  };
  amounts: {
    main: number | null;
    specialProjects: number | null;
    chargeBack: number | null;
    profitCenter: number | null;
    variationOrders: number | null;
  };
  progress: { mobilizationPct: number | null };
  manpower: {
    plannedManual: number | null;
    actualManual: number | null;
    plannedNonManual: number | null;
    actualNonManual: number | null;
    plannedKuwaiti: number | null;
    actualKuwaiti: number | null;
  };
  subcontractors: SubcontractorRow[];
  activities: { planned: string[]; actual: string[] };
  safety: { lostTimeIncidents: number | null };
  quality: { issued: number | null; closed: number | null; pending: number | null };
  overrides: string[];
}

export interface SalesOrderFact {
  docEntry: number;
  docNum: number;
  docDate: string;
  cardName: string;
  numAtCard: string;
  comments: string;
  total: number;
  contractStart: string | null;
  contractEnd: string | null;
  category: KsaCategory;
}

export interface SapFacts {
  readAt: string;
  template: ReportTemplate;
  project: { code: string; name: string; division: string | null; divisionName: string | null; members: Array<{ code: string; name: string; category: KsaCategory }> };
  client: string | null;
  contract: { mainOrder: SalesOrderFact | null; otherOrders: SalesOrderFact[] };
  mcsr: { contractNo: string | null; customer: string | null } | null;
  warnings: string[];
}

export interface ReportView {
  reportId: number;
  companyId: number;
  companyName: string;
  projectCode: string;
  projectName: string;
  month: string;
  monthLabel: string;
  template: ReportTemplate;
  reportNo: number;
  status: ReportStatus;
  data: ReportData;
  facts: SapFacts | null;
  sapReadAt: string | null;
  sapFields: string[];
  sapValues: Record<string, string | number | null>;
  issues: ReportIssue[];
  events: Array<{ action: string; user: string; note: string | null; at: string }>;
  preparedBy: { userId: number | null; name: string };
  submittedBy: string | null;
  submittedAt: string | null;
  returnedBy: string | null;
  returnedAt: string | null;
  returnNote: string | null;
  updatedAt: string;
  previous: { reportId: number; month: string; pendingNcrs: number | null } | null;
  permissions: { canEdit: boolean; canSubmit: boolean; canReturn: boolean; canDelete: boolean };
}

export interface ReportConfig {
  companyId: number;
  companyName: string;
  template: ReportTemplate | null;
  templateLabel: string | null;
  access: { viewAll: boolean; canReturn: boolean; issues: boolean; projects: string[] | null };
}

export interface MonthProjectRow {
  projectCode: string;
  projectName: string;
  active: boolean;
  report: null | {
    reportId: number;
    reportNo: number;
    status: ReportStatus;
    preparedBy: string;
    updatedAt: string;
    submittedAt: string | null;
    issues: number;
    critical: number;
  };
}

export interface IssueRow {
  issueId: number;
  reportId: number;
  companyId: number;
  companyName: string;
  projectCode: string;
  projectName: string;
  month: string;
  reportNo: number;
  section: IssueSection;
  rank: IssueRank;
  description: string;
  action: string;
  status: IssueStatus;
  firstPeriodMonth: string;
  preparedBy: string;
  submittedAt: string | null;
}
